import { useCallback, useEffect, useMemo, useState } from 'react'
import { partialOf } from '../api/answer'
import { Withheld, markPartial } from './Withheld'
import type { Api } from '../api'
import { ApiError, USD, isConverted } from '../api/types'
import type {
  ApiFailure,
  Currency,
  SalespersonReview as ReviewData,
  WeekMember,
  WeekMetric,
} from '../api/types'
import { Button, EmptyState, Loading, money, currencyShort } from './ui'
import { FailureState } from './FailureState'
import { SortHeader, compareBy, useGridSort } from './gridSort'

/**
 * SALESPERSON REVIEW — the one page here that is meant to leave the screen.
 *
 * ---------------------------------------------------------------------------
 * IT PRINTS, SO IT HAS TO SAY WHAT IT IS
 *
 * Everything else in this platform is read in a browser, next to a banner that
 * says whose data it is and in which currency. A printed page carries none of
 * that context and outlives all of it. So the header block states, on the paper:
 * whose figures, which two weeks, which currency, who generated it and when —
 * and, if View As was on, that the figures are somebody else's.
 *
 * That last one is the important one. A printed page of another person's book,
 * handed to a third person, with nothing on it saying so, is the worst outcome
 * View As can produce.
 *
 * ---------------------------------------------------------------------------
 * NO SEPARATE PRINT VIEW
 *
 * The printed page IS this page, restyled by @media print. A second component
 * rendering "the same" figures for paper is a second place for them to be
 * wrong, and the one that gets forgotten is always the printed one.
 *
 * That is also why this screen has no ExportDoc builder to keep in step with
 * the column sort added on 20 Sep 2026: the extract here is the print, the
 * print is this DOM, and the DOM is whatever order the tables are rendered in.
 */

const METRIC_LABEL: Record<WeekMetric, string> = {
  collections: 'Collections',
  sales: 'Sales',
  receivables: 'Receivables',
  overdue: 'Overdue',
}

/** Flow metrics measure activity in the week; as-of metrics a balance at its end. */
const IS_FLOW = (m: WeekMetric) => m === 'collections' || m === 'sales'

const TOP_CUSTOMERS = 10

/** Every heading sorts, the Customer column included — it sorts by name. */
type ReviewSortKey = 'name' | 'a' | 'b' | 'delta'

function weekOf(d: Date): string {
  const c = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))
  c.setUTCDate(c.getUTCDate() - c.getUTCDay())
  return c.toISOString().slice(0, 10)
}

function shiftWeeks(iso: string, n: number): string {
  const d = new Date(iso + 'T00:00:00Z')
  d.setUTCDate(d.getUTCDate() + n * 7)
  return d.toISOString().slice(0, 10)
}

function weekLabel(iso: string): string {
  const d = new Date(iso.slice(0, 10) + 'T00:00:00Z')
  const end = new Date(d)
  end.setUTCDate(end.getUTCDate() + 6)
  const f = (x: Date) =>
    x.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', timeZone: 'UTC' })
  return `${f(d)} – ${f(end)}`
}

interface CustomerRow {
  name: string
  a: number
  b: number
  delta: number
  /** Under the new model, this customer's amount that week is withheld, not absent (M8). */
  aWithheld: boolean
  bWithheld: boolean
  /** Under the new model, this customer's amount that week leaves withheld values out. */
  aPartial: boolean
  bPartial: boolean
}

interface MetricResult {
  key: WeekMetric
  label: string
  isFlow: boolean
  a: number
  b: number
  delta: number
  pct: number | null
  /** True when USD was asked for and this metric can only be served locally. */
  localOnly: boolean
  customers: CustomerRow[]
  /* Under the new model (M8): every amount of a week withheld, or some of them
     withheld or leaving withheld values out, so the totals are partial. */
  withheldA: boolean
  withheldB: boolean
  partial: boolean
}

function pctText(p: number | null): string {
  if (p === null || !Number.isFinite(p)) return '—'
  return `${p > 0 ? '+' : ''}${(p * 100).toFixed(1)}%`
}

function signed(n: number): string {
  return n < 0 ? `−${money(Math.abs(n))}` : `+${money(n)}`
}

export function SalespersonReview({
  api,
  viewAs = null,
  // Defaults to USD with the pages (23 Sep 2026); every parent passes it.
  currency = USD,
  actor,
}: {
  api: Api
  viewAs?: string | null
  currency?: Currency
  /** Who is running this, printed on the page so a sheet of paper is attributable. */
  actor?: string | null
}) {
  const thisWeek = weekOf(new Date())

  const [name, setName] = useState('')
  const [submitted, setSubmitted] = useState('')
  const [weekA, setWeekA] = useState(thisWeek)
  const [weekB, setWeekB] = useState(shiftWeeks(thisWeek, -1))
  const [roster, setRoster] = useState<string[] | null>(null)
  const [data, setData] = useState<ReviewData | null>(null)
  /** The new permission model answered (M8). */
  const [v2, setV2] = useState(false)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  /** Why the roster could not be fetched — for every refusal EXCEPT the masked column. */
  const [rosterFailure, setRosterFailure] = useState<ApiFailure | null>(null)
  /** Bumped by the roster panel's "Try again" to re-run its effect unchanged. */
  const [rosterNonce, setRosterNonce] = useState(0)
  const [loading, setLoading] = useState(false)

  /**
   * The list of names to choose from comes from the SERVER, by asking for
   * receivables grouped by salesperson. A caller who may not see salesperson
   * names is refused that grouping outright, so this returns nothing and the
   * page falls back to a free-text field that will produce identical figures
   * whatever is typed. The roster is never assembled client-side.
   *
   * ONLY that one refusal means "no list to choose from". This catch used to
   * swallow every failure into an empty roster, so a person with no data
   * permissions at all — or a plain 500 — was told names were masked and
   * invited to type one, with no reference number, until Build report finally
   * surfaced the real refusal. Every other failure now shows straight away.
   */
  useEffect(() => {
    let cancelled = false
    // Cleared as the new request starts, not only when one succeeds: otherwise
    // a failure from the previous pair of weeks stays on screen while the new
    // pair is still being asked about, and reads as the answer to it.
    setRosterFailure(null)
    api
      .receivablesWeeks({
        metric: 'receivables',
        dim: 'Salesperson',
        weekA,
        weekB,
        currency,
      })
      .then((r) => {
        if (cancelled) return
        const names = [...new Set(r.data.weekA.members.map((m) => m.member).filter(Boolean))] as string[]
        setRoster(names.sort((x, y) => x.localeCompare(y)))
        setRosterFailure(null)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setRoster([])
        const f: ApiFailure = e instanceof ApiError ? e.failure : { kind: 'error', title: 'Unexpected error.' }
        // A MASKED COLUMN IS NOT A REFUSED REPORT. DENIED_NO_COLUMN means this
        // reader's modules hide salesperson names, so the roster is withheld
        // while the rest of the review stands — not a failure to show.
        //
        // MATCHED ON THE REASON CODE, never the title. This used to match the
        // title "Access refused", with a comment warning it must move to the
        // code if the refusal was ever named. On 22 Sep 2026 the API gave
        // DENIED_NO_COLUMN a proper title for Week Comparison's sake, and this
        // screen broke without a line of it changing: a receivables reader
        // without SLS_Mod would have seen a refusal panel instead of a review.
        setRosterFailure(f.kind === 'forbidden' && f.reason === 'DENIED_NO_COLUMN' ? null : f)
      })
    return () => {
      cancelled = true
    }
  }, [api, weekA, weekB, currency, viewAs, rosterNonce])

  const load = useCallback(
    async (who: string) => {
      setLoading(true)
      setFailure(null)
      try {
        const res = await api.salespersonReview({ salesperson: who, weekA, weekB, currency })
        setData(res.data)
        setV2(res.vis !== null)
        setSubmitted(who)
      } catch (e: unknown) {
        setFailure(e instanceof ApiError ? e.failure : { kind: 'error', title: 'Unexpected error.' })
      } finally {
        setLoading(false)
      }
    },
    [api, weekA, weekB, currency, viewAs],
  )

  // Re-run when the weeks or the currency change, but only once a person has
  // actually been chosen — otherwise every keystroke would be an audited read.
  useEffect(() => {
    if (submitted) void load(submitted)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekA, weekB, currency, viewAs])

  const results = useMemo<MetricResult[]>(() => {
    if (!data) return []
    return data.metrics.map((m) => {
      // Absent from a week is zero; under the new model a blank amount is
      // withheld instead, and is kept apart (M8).
      const merge = (rows: WeekMember[]) => {
        const map = new Map<string, number | null>()
        for (const r of rows) map.set(r.member ?? '(blank)', v2 ? r.amount : r.amount ?? 0)
        return map
      }
      const ma = merge(m.weekA)
      const mb = merge(m.weekB)
      const partialIn = (rows: WeekMember[]) =>
        new Set(rows.filter((r) => partialOf(r).has('amount')).map((r) => r.member ?? '(blank)'))
      const pa = partialIn(m.weekA)
      const pb = partialIn(m.weekB)

      // COLLECTIONS IS SERVED IN LOCAL CURRENCY whatever the toggle says: the
      // payments table has no rate and no dollar column. Matching the report
      // this replaces (decided 5 Aug 2026), the figure is shown and captioned
      // rather than withheld — so on paper the caption is the only thing
      // separating it from a converted number, and it prints beside the total.
      const localOnly = m.metric === 'collections' && isConverted(currency)

      const names = new Set([...ma.keys(), ...mb.keys()])
      const customers: CustomerRow[] = [...names].map((n) => {
        const a = ma.get(n) ?? 0
        const b = mb.get(n) ?? 0
        return {
          name: n, a, b, delta: a - b, aWithheld: ma.get(n) === null, bWithheld: mb.get(n) === null,
          aPartial: pa.has(n), bPartial: pb.has(n),
        }
      })
      // A customer withheld in either week has no change to rank by: last (16c's review).
      const unknown = (c: CustomerRow) => Number(c.aWithheld || c.bWithheld)
      customers.sort((x, y) => unknown(x) - unknown(y) || Math.abs(y.delta) - Math.abs(x.delta))
      // Under the new model the change is the sum of the changes that show.
      const known = customers.filter((c) => !c.aWithheld && !c.bWithheld)
      const knownB = known.reduce((s, c) => s + c.b, 0)
      const knownDelta = known.reduce((s, c) => s + c.delta, 0)

      const a = customers.reduce((s, c) => s + c.a, 0)
      const b = customers.reduce((s, c) => s + c.b, 0)
      return {
        key: m.metric,
        label: METRIC_LABEL[m.metric],
        isFlow: IS_FLOW(m.metric),
        a,
        b,
        delta: v2 ? knownDelta : a - b,
        pct: v2 ? (knownB !== 0 ? knownDelta / Math.abs(knownB) : null) : b !== 0 ? (a - b) / Math.abs(b) : null,
        localOnly,
        customers,
        withheldA: v2 && m.weekA.length > 0 && m.weekA.every((x) => x.amount === null),
        withheldB: v2 && m.weekB.length > 0 && m.weekB.every((x) => x.amount === null),
        partial: v2 && [...m.weekA, ...m.weekB].some((x) => x.amount === null || partialOf(x).size > 0),
      }
    })
  }, [data, v2])

  /**
   * Sentences generated from the figures on the page — never from anything the
   * reader cannot see. Each one is checkable against a table below it, which is
   * the only kind of "insight" worth printing.
   */
  const insights = useMemo(() => {
    const out: string[] = []
    for (const r of results) {
      // Nothing is skipped now: every metric has a figure -- except, under the
      // new model, one this reader may not see either week (M8).
      if (r.withheldA || r.withheldB) continue
      const dir = r.delta > 0 ? 'rose' : r.delta < 0 ? 'fell' : 'was unchanged'
      out.push(
        `${r.label}${r.isFlow ? '' : ' balance'} ${dir} ${pctText(r.pct)} (${money(r.b)} → ${money(r.a)})${r.partial ? ', partly withheld' : ''}.`,
      )
    }
    const rec = results.find((r) => r.key === 'receivables')
    const ovd = results.find((r) => r.key === 'overdue')
    // Never a ratio of a withheld or partial figure: a withheld amount reads as 0 (16c's review).
    if (rec && ovd && rec.a > 0 && !(rec.partial || ovd.partial || rec.withheldA || ovd.withheldA)) {
      out.push(`Overdue is ${((ovd.a / rec.a) * 100).toFixed(1)}% of the balance at week end.`)
    }
    const col = results.find((r) => r.key === 'collections')
    if (col) {
      const shown = col.customers.filter((c) => !c.aWithheld && !c.bWithheld)
      const up = shown.find((c) => c.delta > 0)
      const down = shown.find((c) => c.delta < 0)
      if (up) out.push(`Largest collections gain: ${up.name} (${signed(up.delta)}).`)
      if (down) out.push(`Largest collections drop: ${down.name} (${signed(down.delta)}).`)
    }
    return out
  }, [results])

  return (
    <div>
      {/* ---------------- Controls. Not printed. ---------------- */}
      <div className="no-print flex flex-wrap items-end gap-x-5 gap-y-3 border-b border-(--color-line) px-5 py-4">
        {/* On a phone the 288px box and its button do not fit one line, so
            under a 768px content box the box takes the width and the button
            drops beneath it. */}
        <div className="@max-3xl:w-full">
          <div className="eyebrow mb-1.5">Salesperson</div>
          <div className="flex items-center gap-2 @max-3xl:flex-wrap">
            <input
              list="mz-roster"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && name.trim()) void load(name.trim())
              }}
              placeholder={roster && roster.length > 0 ? 'Start typing a name…' : 'Enter a name…'}
              className="h-9 w-72 rounded border border-(--color-line-strong) bg-(--color-surface) px-3 text-(length:--text-base) text-(--color-ink) outline-none focus:border-(--color-accent) @max-3xl:w-full"
            />
            <datalist id="mz-roster">
              {(roster ?? []).map((r) => (
                <option key={r} value={r} />
              ))}
            </datalist>
            <Button onClick={() => name.trim() && void load(name.trim())} disabled={!name.trim()}>
              Build report
            </Button>
          </div>
        </div>

        <div>
          <div className="eyebrow mb-1.5">This week</div>
          <WeekStep value={weekA} onChange={setWeekA} />
        </div>
        <div>
          <div className="eyebrow mb-1.5">Compared with</div>
          <WeekStep value={weekB} onChange={setWeekB} />
        </div>

        {data && (
          <Button variant="primary" onClick={() => window.print()}>
            Print
          </Button>
        )}
      </div>

      {failure ? (
        <FailureState failure={failure} onRetry={() => submitted && void load(submitted)} />
      ) : loading ? (
        /* Ahead of the roster failure: pressing Build report after the roster
           failed must still show that something is happening. */
        <Loading label="Building the review…" />
      ) : rosterFailure && !data ? (
        /* With a retry: the roster is a Heavy procedure, so a transient
           admission-control shed is a real possibility, and without this the
           only way to ask again was to change a week. */
        <FailureState failure={rosterFailure} onRetry={() => setRosterNonce((n) => n + 1)} />
      ) : !data ? (
        <EmptyState
          title="Choose a salesperson"
          detail={
            roster === null
              ? 'Loading the names you are permitted to see…'
              : roster.length > 0
                ? 'Pick a name and two weeks. The report covers collections, sales, receivables and overdue, each broken down by customer.'
                : 'Salesperson names are not shown to you, so there is no list to choose from. Any name entered will return the same figures — the filter is not applied.'
          }
        />
      ) : (
        /* ---------------- The report. This is what prints. ---------------- */
        <article className="sheet px-5 py-6">
          <header className="mb-6 border-b-2 border-(--color-ink) pb-4">
            <div className="eyebrow">Salesperson performance review</div>
            <h2 className="mt-1 text-(length:--text-xl) font-semibold tracking-[-0.02em] text-(--color-ink)">
              {data.salesperson}
            </h2>
            <p className="mt-2 text-(length:--text-base) text-(--color-ink-2)">
              <b>This week</b> {weekLabel(data.weekA.start)} &nbsp;vs&nbsp;{' '}
              <b>compared with</b> {weekLabel(data.weekB.start)}
              {results.some((r) => !r.isFlow) && (
                <> · balances as at each week end</>
              )}
            </p>
            <p className="mt-1 text-(length:--text-small) text-(--color-ink-3)">
              {currencyShort(currency)} ·
              Generated {new Date().toLocaleString('en-GB')}
              {actor ? ` by ${actor}` : ''} · MBI
            </p>
            {/* On paper this is the only thing standing between a printed page
                and somebody believing it is their own book. */}
            {viewAs && (
              <p className="mt-2 border-l-4 border-(--color-privileged) bg-(--color-surface-3) px-3 py-2 text-(length:--text-small) font-semibold text-(--color-privileged)">
                Produced while signed in as {viewAs}. Every figure below is what THEY are
                permitted to see — not the person who generated this page.
              </p>
            )}
          </header>

          {/* Scorecard */}
          <div className="mb-6 grid grid-cols-1 gap-3 @2xl:grid-cols-2">
            {results.map((r) => (
              <Scorecard key={r.key} r={r} />
            ))}
          </div>

          {insights.length > 0 && (
            <section className="mb-6 break-inside-avoid">
              <div className="eyebrow mb-2">Summary</div>
              <ul className="list-disc space-y-1 pl-5 text-(length:--text-body) leading-relaxed text-(--color-ink-2)">
                {insights.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ul>
            </section>
          )}

          {results.map((r) => (
            <CustomerTable key={r.key} r={r} a={data.weekA.start} b={data.weekB.start} />
          ))}
        </article>
      )}
    </div>
  )
}

/** More collections and sales is good; more receivables and overdue is not. */
function toneFor(key: WeekMetric, delta: number): string {
  if (delta === 0) return 'text-(--color-ink-3)'
  const goodWhenUp = key === 'collections' || key === 'sales'
  return (delta > 0) === goodWhenUp ? 'text-(--color-good)' : 'text-(--color-overdue)'
}

function Scorecard({ r }: { r: MetricResult }) {
  const max = Math.max(Math.abs(r.a), Math.abs(r.b), 1)
  const bar = (v: number) => `${Math.max(1, (Math.abs(v) / max) * 100)}%`

  return (
    <div className="break-inside-avoid rounded-lg border border-(--color-line) bg-(--color-surface-2) px-4 py-3.5">
      <div className="flex items-baseline justify-between">
        <div className="font-semibold text-(--color-ink)">{r.label}</div>
        <div className={`num font-semibold ${toneFor(r.key, r.delta)}`}>{pctText(r.pct)}</div>
      </div>
      {/* Load-bearing on paper. These figures are local while the header says
          US dollars, and this line is the only thing that says so. */}
      {r.localOnly && (
        <div className="mt-1 text-(length:--text-small) font-semibold text-(--color-warn)">
          In local currency — no US dollar figure exists for this measure.
        </div>
      )}
      <div className="mt-2.5 flex gap-5">
        <div>
          <div className="eyebrow">This week</div>
          <div className="num text-(length:--text-lg) font-semibold text-(--color-ink)">{r.withheldA ? <Withheld /> : markPartial(r.partial, money(r.a))}</div>
        </div>
        <div>
          <div className="eyebrow">Compared with</div>
          <div className="num text-(length:--text-lg) font-semibold text-(--color-ink-2)">{r.withheldB ? <Withheld /> : markPartial(r.partial, money(r.b))}</div>
        </div>
        <div className="ml-auto text-right">
          <div className="eyebrow">Change</div>
          <div className={`num text-(length:--text-lg) font-semibold ${toneFor(r.key, r.delta)}`}>
            {r.withheldA || r.withheldB ? <Withheld /> : markPartial(r.partial, signed(r.delta))}
          </div>
        </div>
      </div>
      <div className="mt-3 space-y-1">
        {([['A', r.a], ['B', r.b]] as const).map(([lbl, v]) => (
          <div key={lbl} className="flex items-center gap-2">
            <span className="w-3 text-(length:--text-small) text-(--color-ink-3)">{lbl}</span>
            <span className="h-2 flex-1 overflow-hidden rounded-full bg-(--color-line)">
              <span
                className="block h-full rounded-full bg-(--color-navy-700)"
                style={{ width: bar(v) }}
              />
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

function CustomerTable({ r, a, b }: { r: MetricResult; a: string; b: string }) {
  /* Each section keeps its OWN sort. Four metrics print one under another and
     they are four separate questions; sorting collections by name has no
     business reordering the overdue table underneath it. */
  const { sort, toggle, ariaSort } = useGridSort<ReviewSortKey>()

  /**
   * THE SLICE IS TAKEN FIRST, THE SORT SECOND.
   *
   * WHICH ten customers appear is decided by the ranking `results` built —
   * the ten largest movements — and that must not change when a reader sorts.
   * Sorting before slicing would make the heading above ("10 largest
   * movements") a lie and would swap the folded remainder's membership under
   * the reader, so the "Other customers" row would stop reconciling to the
   * Total. Slicing first means a sort can only reorder the ten rows already
   * chosen; the same ten always add up to the same figures.
   *
   * The two summary rows below are NOT in this array and are rendered after
   * the map, so they stay pinned to the bottom in their fixed order: the
   * folded remainder, then the Total. Neither is a customer, and a Total that
   * sorted itself into the middle of a column of customers would be read as
   * one of them.
   */
  const top = r.customers.slice(0, TOP_CUSTOMERS)
  const shown = sort.key
    ? [...top].sort((x, y) => {
        const k = sort.key as ReviewSortKey
        return compareBy(x[k], y[k], sort.desc, () => x.name.localeCompare(y.name))
      })
    : top
  const rest = r.customers.slice(TOP_CUSTOMERS)
  const restA = rest.reduce((s, c) => s + c.a, 0)
  const restB = rest.reduce((s, c) => s + c.b, 0)

  if (shown.length === 0) return null

  return (
    <section className="mb-6 break-inside-avoid">
      <div className="mb-1.5 text-(length:--text-base) font-semibold text-(--color-ink)">
        {r.label} by customer{' '}
        <span className="font-normal text-(--color-ink-3)">
          · {Math.min(TOP_CUSTOMERS, r.customers.length)} largest movements
        </span>
        {r.localOnly && (
          <span className="ml-2 font-semibold text-(--color-warn)">· local currency</span>
        )}
      </div>
      <table className="report-table w-full border-collapse text-(length:--text-body)">
        <thead>
          {/* `eyebrow` moved from the label onto the CELL: it only ever carried
              typography, all of it inherited, so the heading sets exactly as
              before while the label inside becomes SortHeader's button. */}
          <tr>
            {([
              { key: 'name', label: 'Customer', measureKey: 'customer' },
              { key: 'a', label: weekLabel(a), measureKey: 'weekA' },
              { key: 'b', label: weekLabel(b), measureKey: 'weekB' },
              { key: 'delta', label: 'Change', measureKey: 'change' },
            ] as const).map((col) => (
              <th
                key={col.key}
                scope="col"
                aria-sort={ariaSort(col.key)}
                className="eyebrow border-b border-(--color-line-strong) px-3 py-1.5 text-center"
              >
                <SortHeader
                  label={col.label}
                  active={sort.key === col.key}
                  desc={sort.desc}
                  onToggle={() => toggle(col.key)}
                  surface="review"
                  measureKey={col.measureKey}
                />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {shown.map((c) => (
            <tr key={c.name} className="border-b border-(--color-line)">
              <td className="px-3 py-1.5 text-(--color-ink)">{c.name}</td>
              <td className="num px-3 py-1.5 text-(--color-ink)">{c.aWithheld ? <Withheld /> : markPartial(c.aPartial, money(c.a))}</td>
              <td className="num px-3 py-1.5 text-(--color-ink-2)">{c.bWithheld ? <Withheld /> : markPartial(c.bPartial, money(c.b))}</td>
              <td className={`num px-3 py-1.5 font-semibold ${toneFor(r.key, c.delta)}`}>
                {c.aWithheld || c.bWithheld ? <Withheld /> : signed(c.delta)}
              </td>
            </tr>
          ))}
          {rest.length > 0 && (
            <tr className="border-b border-(--color-line) text-(--color-ink-3)">
              <td className="px-3 py-1.5">Other customers ({rest.length})</td>
              <td className="num px-3 py-1.5">{markPartial(rest.some((c) => c.aWithheld || c.aPartial), money(restA))}</td>
              <td className="num px-3 py-1.5">{markPartial(rest.some((c) => c.bWithheld || c.bPartial), money(restB))}</td>
              <td className="num px-3 py-1.5">{markPartial(rest.some((c) => c.aWithheld || c.bWithheld), signed(restA - restB))}</td>
            </tr>
          )}
          <tr className="border-t-2 border-(--color-line-strong) font-semibold">
            <td className="px-3 py-2 text-(--color-ink)">Total</td>
            <td className="num px-3 py-2 text-(--color-ink)">{r.withheldA ? <Withheld /> : markPartial(r.partial, money(r.a))}</td>
            <td className="num px-3 py-2 text-(--color-ink)">{r.withheldB ? <Withheld /> : markPartial(r.partial, money(r.b))}</td>
            <td className={`num px-3 py-2 ${toneFor(r.key, r.delta)}`}>{r.withheldA || r.withheldB ? <Withheld /> : markPartial(r.partial, signed(r.delta))}</td>
          </tr>
        </tbody>
      </table>
    </section>
  )
}

function WeekStep({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={() => onChange(shiftWeeks(value, -1))}
        title="Previous week"
        className="h-9 w-9 rounded border border-(--color-line-strong) bg-(--color-surface) text-(--color-ink-2) transition-colors hover:bg-(--color-surface-3)"
      >
        ‹
      </button>
      <span className="min-w-[130px] px-2 text-center text-(length:--text-base) font-medium text-(--color-ink)">
        {weekLabel(value)}
      </span>
      <button
        type="button"
        onClick={() => onChange(shiftWeeks(value, 1))}
        title="Next week"
        className="h-9 w-9 rounded border border-(--color-line-strong) bg-(--color-surface) text-(--color-ink-2) transition-colors hover:bg-(--color-surface-3)"
      >
        ›
      </button>
    </div>
  )
}
